import { useState, useMemo } from 'react';
import { TableAction, TableColumn } from '@/types';
import { IssueReport } from '@/types/model';
import IssueStatusBadge from '@/components/ui/IssueStatusBadge';
import ContentTypeBadge from '@/components/ui/ContentTypeBadge';
import { Eye, CheckCircle } from 'lucide-react';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import { TitleCell, ErrorTypeCell } from './mediaErrorHelpers';
import { useConfirmDialog, useTranslation } from '@/hooks';
import toast from 'react-hot-toast';
import {
  useIssueReports,
  useIssueReportCounts,
  useMarkIssueAsResolved,
} from '@/hooks';

type IssueTab = 'reportados' | 'reparados' | 'todos';

const Issues = () => {
  const { t } = useTranslation();
  const { confirm, dialog } = useConfirmDialog();
  const [activeTab, setActiveTab] = useState<IssueTab>('todos');

  const { data: counts } = useIssueReportCounts();

  const markAsResolvedMutation = useMarkIssueAsResolved();

  // Filter based on active tab
  const tabFilters = useMemo(() => {
    switch (activeTab) {
      case 'reportados':
        return { status: 'OPEN' };
      case 'reparados':
        return { status: 'RESOLVED' };
      case 'todos':
      default:
        return {};
    }
  }, [activeTab]);

  const issueColumns: TableColumn<IssueReport>[] = [
    {
      key: 'mediaType',
      className: 'w-12',
      sortable: true,
      header: t.issueReports.columns.content,
      accessor: (issue) => (
        <ContentTypeBadge type={issue.mediaType} />
      )
    },
    {
      key: 'title',
      header: t.issueReports.columns.title,
      accessor: (issue) => <TitleCell row={issue} />,
      className: 'truncate max-w-72'
    },
    {
      key: 'errorType',
      sortable: true,
      header: t.issueReports.columns.type,
      accessor: (issue) => (
        <span className="text-gray-900">
          <ErrorTypeCell type={issue.errorType}/>
        </span>
      )
    },
    {
      key: 'occurrencesCount',
      sortable: true,
      header: t.issueReports.columns.occurrenceCount,
      accessor: (issue) => (
        <span className="text-gray-900">
          {issue.occurrencesCount}
        </span>
      ),
    },
    {
      sortable: true,
      key: 'createdDate',
      header: t.issueReports.columns.date,
      accessor: (issue) => (
        <span className="text-gray-900">
          {new Date(issue.createdDate).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })}
        </span>
      ),
      className: 'min-w-32'
    },
    {
      sortable: true,
      key: 'status',
      header: t.issueReports.columns.status,
      accessor: (issue) => <IssueStatusBadge status={issue.status} />
    }
  ];

  const issueActions: TableAction<IssueReport>[] = [
    {
      label: t.issueReports.actions.view,
      icon: Eye,
      onClick: (issue) => console.log('View issue:', issue.id),
      className: 'text-gray-700 hover:text-indigo-600'
    },
    {
      label: t.issueReports.actions.markAsResolved,
      icon: CheckCircle,
      onClick: async (issue) => {
        try {
          const confirmed = await confirm({
            title: t.common.confirmationTitle,
            message: t.issueReports.messages.confirmResolve,
            confirmLabel: t.issueReports.actions.markAsResolved,
            cancelLabel: t.buttons.cancel,
            onConfirm: async () => {
              await markAsResolvedMutation.mutateAsync({ id: issue.id });
            },
          });

          if (!confirmed) {
            return;
          }
          toast.success(t.issueReports.messages.resolved);
        } catch (err) {
          console.error('Mark as resolved error', err);
          toast.error(err instanceof Error ? err.message : t.errors.unexpectedError);
        }
      },
      className: 'text-gray-700 hover:text-green-600',
      show: (issue) => issue.status !== 'RESOLVED'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">
              {t.issueReports.title}
            </h1>
            <div className="text-lg font-semibold text-orange-600">
              {t.issueReports.total} {counts?.total?.toLocaleString() || 0}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
            <button
              onClick={() => setActiveTab('reportados')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'reportados'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {t.issueReports.tabs.reportados}
            </button>
            <button
              onClick={() => setActiveTab('reparados')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'reparados'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {t.issueReports.tabs.reparados}
            </button>
            <button
              onClick={() => setActiveTab('todos')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'todos'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {t.issueReports.tabs.todos}
            </button>
          </div>
        </div>

        <DataTableWithQuery
          showTotalCount={true}
          columns={issueColumns}
          actions={issueActions}
          useQueryHook={useIssueReports}
          searchPlaceholder={t.issueReports.searchPlaceholder}
          title={t.issueReports.listTitle}
          itemsPerPage={10}
          initialFilters={tabFilters}
        />
        {dialog}
      </div>
    </div>
  );
}

export default Issues;
